import {useReducer, useRef} from 'preact/hooks';
import {createSearchContext} from '@ultradent/upi-fuzzy-search'
import {useEffect} from 'react';
import {getSearchParams} from '@util/URLUtils';
import {toQueryString} from '@util/ObjectUtils';
import {useLocation, useNavigate} from 'react-router-dom';
import equals from 'ramda/src/equals';
import compose from 'ramda/src/compose';
import not from 'ramda/src/not';
import {isEmptyOrNil, notEmptyOrNil} from '@ultradent/utilities/EmptyOrNil';

const itemsPerPage = 15;
const isMarketingMaterial = item => item.productFamily === 'Marketing Materials';
const isSample = item => /^s\d+/i.test( item.sku );
const isNotMarketingMaterial = compose( not, isMarketingMaterial );
const isNotSample = compose( not, isSample );

const INITIAL_STATE = {
    initialized: false,
    resultsAll: [],
    resultsPaged: [],
    page: 1,
    productCategory: null,
    productLanguage: null,
    keyword: null
};
const queryReducer = ( state, action ) => {
    let results = [];
    let page = 1;

    switch ( action.type ) {
        case 'QUERY':
            results = action.results || state.resultsAll;
            page = action.page || state.page;

            return {
                ...state,
                page,
                initialized: action.initialized,
                resultsAll: results,
                resultsPaged: results.slice( 0, itemsPerPage * page ),
                productCategory: action.productCategory || state.productCategory,
                productLanguage: action.productLanguage || state.productLanguage,
                keyword: action.keyword
            };

        case 'INCREMENT_PAGE':
            const nextPage = state.page + 1;

            return {
                ...state,
                page: nextPage,
                resultsPaged: state.resultsAll.slice( 0, itemsPerPage * nextPage )
            };
        default:
            return state;
    }
}

const newSearchCtx = d => createSearchContext( d || [], ['kitName', 'sku', 'productFamily'] );

function mapQueryParams ( params ) {
    return {
        brandId: params.brandid,
        productCategory: params.productcategory,
        productLanguage: params.productlanguage,
        keyword: params.keyword
    };
};
const getQueryFromUrl = compose( mapQueryParams, getSearchParams );

const useProductDocumentationSearch = ( {data} ) => {

    const prevData = useRef( data );
    const searchCtx = useRef( newSearchCtx( data ) );
    const navigate = useNavigate();
    const location = useLocation();
    const initialQuery = getQueryFromUrl( location.search );

    const [query, dispatch] = useReducer( queryReducer, {
        ...INITIAL_STATE,
        ...initialQuery,
        page: 1
    } );

    useEffect( () => {
        if ( prevData.current !== data ) {
            prevData.current = data;
            searchCtx.current = newSearchCtx( data );
            searchCtx.current.setFilters( [isNotMarketingMaterial, isNotSample] );
            doQuery( initialQuery );
        }
    }, [data] );

    useEffect( () => {
            const nextQuery = getQueryFromUrl( location.search );
            const previousQuery = {
                productCategory: query.productCategory,
                productLanguage: query.productLanguage,
                keyword: query.keyword
            };

            if ( query.initialized && !equals( nextQuery, previousQuery ) ) {
                doQuery( nextQuery );
            }
        },
        [location.search,
            query.keyword,
            query.initialized,
            query.productCategory,
            query.productLanguage] );

    function doQuery ( {brandId, productCategory, productLanguage, keyword} ) {
        const {results} = searchCtx.current.query( keyword || '' );

        // note -> brandId is only issued once on page load if present in queryString; All new queries will clear this filter
        const filteredResults = notEmptyOrNil( brandId )
            ? results.filter( item => item.brandId === parseInt( brandId ) )
            : results;

        dispatch( {
            initialized: true,
            type: 'QUERY',
            productCategory,
            productLanguage,
            keyword,
            results: filteredResults,
            page: 1
        } );
    }

    return {
        isLoading: !query.initialized,
        hasError: query.initialized && isEmptyOrNil( query.resultsAll ),
        results: query.resultsPaged,
        currentQuery: query,
        onQuery: ( {productCategory, productLanguage, keyword} ) => {
            const nextSearch = toQueryString( {productCategory, productLanguage, keyword} );
            navigate( `./?${nextSearch}` );
            doQuery( {productCategory, productLanguage, keyword} );
        },
        onShowMore: () => {
            dispatch( {
                type: 'INCREMENT_PAGE'
            } );
        },
        doShowMoreAction: query.resultsAll?.length > query.resultsPaged?.length
    }
}

export default useProductDocumentationSearch;